import type { Destination, Transport } from '../types';
import { transportDuringVisit } from './destinationItems';
import { journeyFor } from './journeys';
import { cityLabel, matchesCity } from './placeNames';
import { addDays } from './travelState';

/**
 * Where a saved transport sits on the itinerary — shared by the timeline (which
 * draws it) and Save (which refuses one that would land nowhere).
 *
 * A leg sits in a transfer window — before the trip, after it, or between two
 * consecutive destinations — when its dates overlap the window. Two kinds of
 * leeway, because a strictly calendar window orphaned real journeys:
 *   - one day past the window's end, for a leg that is a connection or that
 *     arrives in the next destination (the 01:55 flight on the day after the
 *     next stop "starts");
 *   - a journey travels together: if any leg of a connecting journey is in the
 *     window, every leg is.
 * Legs during a stay (excursions, local hops) and rentals belong to the stay,
 * not to a window.
 */
type Leg = Transport & { _dayDate?: string };
type Visit = Pick<Destination, 'name' | 'arrivalDate' | 'departureDate'>;

const dateOf = (t: Leg) => (t.dep ? t.dep.slice(0, 10) : t._dayDate || '');
const duringAnyStay = (t: Leg, dests: Visit[], all: Leg[]) =>
  dests.some(d => transportDuringVisit(t, d as Destination, all));
const byDeparture = (list: Leg[]) => [...list].sort((a, b) => (a.dep || a._dayDate || '').localeCompare(b.dep || b._dayDate || ''));

function inWindow(t: Leg, after: string | null, before: string | null, next?: Visit): boolean {
  const from = dateOf(t);
  if (!from) return false;
  const arrives = (t.arr || '').slice(0, 10);
  const to = arrives > from ? arrives : from;
  if (after && to < after) return false;
  if (before && from > before) {
    const arrivesInNext = !!next?.name && matchesCity(t.toCity || cityLabel(t.to || ''), next.name);
    if (from > addDays(before, 1) || !(t.isConnection || arrivesInNext)) return false;
  }
  return true;
}

/** Legs in the window [after, before] (null = open), journeys kept together. `all` should be sorted by departure. */
export function transportsInWindow<T extends Leg>(all: T[], dests: Visit[], after: string | null, before: string | null, next?: Visit): T[] {
  const eligible = (t: T) => !t.isRental && !duringAnyStay(t, dests, all);
  const hits = new Set(all.filter(t => eligible(t) && inWindow(t, after, before, next)));
  for (const t of [...hits]) for (const l of journeyFor(t, all)) if (eligible(l)) hits.add(l);
  return all.filter(t => hits.has(t));
}

/** Would this transport land anywhere on the itinerary? (Save requires it.) */
export function isOnItinerary(t: Leg, trip: { startDate?: string; endDate?: string; destinations?: Visit[] }, all: Leg[]): boolean {
  const dests = [...(trip.destinations || [])].filter(d => d.arrivalDate).sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  const legs = byDeparture(all.includes(t) ? all : [...all, t]);
  if (t.isRental || duringAnyStay(t, dests, legs)) return true;
  if (trip.startDate && transportsInWindow(legs, dests, null, trip.startDate).includes(t)) return true;
  if (trip.endDate && transportsInWindow(legs, dests, trip.endDate, null).includes(t)) return true;
  return dests.some((b, i) => i > 0 && transportsInWindow(legs, dests, dests[i - 1].departureDate, b.arrivalDate, b).includes(t));
}
